import React, { useState } from "react";
import Modal from "../Shared/Modal";
import { Table } from "../Shared/Table/Table";
import { INurtureCampaignRecipientStatistic } from "../../models/nurtureCampaign/NurtureCampaignStatistics";
import useUserContext from "../../contexts/UserContext";
import { createColumnHelper } from "@tanstack/react-table";
import { ButtonInput } from "../Shared/Inputs/ButtonInput";
import formatDateTime from "../../utils/formatDateTime";
import * as Papa from "papaparse";
import { DateTime } from "luxon";
import useApiHelper from "../../services/useApiHelper";
import useQSBuilder from "../../hooks/useQSBuilder";
import LoadingWheel from "../Shared/LoadingWheel";
import { DocumentIcon } from "@heroicons/react/outline";

interface NewsletterRecipientsModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  date: Date;
}

const colHelper = createColumnHelper<INurtureCampaignRecipientStatistic>();

export function NewsletterRecipientsModal({ open, setOpen, date }: NewsletterRecipientsModalProps) {
  const { get } = useApiHelper();
  const { user: { agency } } = useUserContext();
  const [exportLoading, setExportLoading] = useState(false);
  const buildQs = useQSBuilder();

  const handleExportCSV = async () => {
    setExportLoading(true);

    try {
      const qs = buildQs({ date: date.toISOString(), itemsPerPage: 1000000 })
      const res = await get(`/marketing/${agency.id}/newsletter/recipients${qs}`);

      parseAndDownloadCSV(res.data);

    } catch (error) {
      console.error("Error exporting CSV:", error);
    } finally {
      setExportLoading(false);
    }
  };

  const parseAndDownloadCSV = (data: INurtureCampaignRecipientStatistic[]) => {
    const csv = Papa.unparse(data);

    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");

    a.href = url;
    a.download = `newsletter_recipients-${formatDateTime(new Date().toISOString(), DateTime.DATE_SHORT)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const cols = [
    colHelper.accessor("email", {
      header: "Email",
    }),
    colHelper.accessor("firstName", {
      header: "First Name",
    }),
    colHelper.accessor("lastname", {
      header: "Last Name",
    }),
    colHelper.accessor("openCount", {
      header: "Opens",
    }),
    colHelper.accessor("clickCount", {
      header: "Clicks",
    }),
  ];

  return (
    <Modal
      // @ts-ignore
      title="Newsletter Recipients"
      open={open}
      setOpen={() => setOpen(false)}
      width="max-w-6xl"
    >
      <div className="flex flex-row items-center justify-between">
        <p className="text-xl font-medium">Recipient List</p>
        {/* @ts-ignore */}
        <ButtonInput
          onClick={handleExportCSV}
          disabled={exportLoading}
        >
          <div className="flex flex-row items-center space-x-2">
            {exportLoading ? <LoadingWheel width="w-5" height="h-5" /> : <DocumentIcon className="w-5 h-5" />}
            <span>{exportLoading ? "Exporting..." : "Export CSV"}</span>
          </div>
        </ButtonInput>
      </div>
      <div>
        <Table<INurtureCampaignRecipientStatistic>
          url={`/marketing/${agency.id}/newsletter/recipients`}
          queryString={{ date: date.toISOString() }}
          queryKey={["newsletterRecipients", agency.id]}
          columns={cols}
        />
      </div>
    </Modal>
  );
}
